<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <loading :status="loading.form"/>
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex items-center gap-3">
                        <h1 class="stage-page-title">Ticket</h1>
                        <identifier-badge :value="ticket.id || '-'" title="Ticket-ID"></identifier-badge>
                    </div>
                </div>
                <div v-on-clickaway="closeActionDropdown" class="stage-page-header-actions">
                    <button class="btn-stage-icon-plain" type="button" title="Zurück" aria-label="Zurück" @click="$router.push(ticketListPath)">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-left"></svg-vue>
                    </button>
                    <div v-if="aiEnabled" class="relative inline-flex items-center">
                        <button
                            class="btn-stage-icon-plain"
                            type="button"
                            title="KI-Assistent"
                            aria-label="KI-Assistent"
                            @click="toggleAiSidebar"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.hexagon-nodes"></svg-vue>
                        </button>
                    </div>
                    <div v-if="canManageTicketTopic" class="relative inline-flex items-center">
                                    <button class="btn-stage-icon-plain" type="button" :title="$t('TicketTopic')" :aria-label="$t('TicketTopic')" @click="toggleActionDropdown('ticketTopic')">
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.text"></svg-vue>
                                    </button>
                                    <div v-show="actions.ticketTopic" class="origin-top-right absolute top-full right-0 mt-2 w-56 rounded shadow-lg z-20">
                                        <div class="rounded bg-white shadow-xs">
                                            <div class="stage-icon-menu-header">{{ $t('TicketTopic') }}</div>
                                            <div class="py-1">
                                                <template v-for="ticketTopic in ticketTopicList" :key="'ticket-topic-option-' + ticketTopic.id">
                                                    <label
                                                       
                                                        class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                                    >
                                                        <input
                                                            v-model="selectedTicketTopicId"
                                                            :value="ticketTopic.id"
                                                            class="form-radio h-4 w-4 text-gray-700 mr-3"
                                                            type="radio"
                                                            @change="updateTicketTopic"
                                                        >
                                                        <span class="truncate">{{ ticketTopic.name }}</span>
                                                    </label>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                    <div v-if="canManageLabels" class="relative inline-flex items-center">
                        <button class="btn-stage-icon-plain" type="button" title="Labels" aria-label="Labels" @click="toggleActionDropdown('label')">
                            <svg-vue class="h-4 w-5" icon="font-awesome.regular.label"></svg-vue>
                        </button>
                        <div v-show="actions.label" class="origin-top-right absolute top-full right-0 mt-2 w-56 rounded shadow-lg z-20">
                            <div class="rounded bg-white shadow-xs">
                                <div class="stage-icon-menu-header">{{ $t('Labels') }}</div>
                                <div class="py-1">
                                    <template v-for="label in labelList" :key="'label-option-' + label.id">
                                        <button
                                            type="button"
                                            class="flex w-full items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                            @click="toggleTicketLabel(label)"
                                        >
                                            <span class="truncate">{{ label.name }}</span>
                                        </button>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="isAdmin" class="relative inline-flex items-center">
                                    <button class="btn-stage-icon-plain" type="button" title="Projektzuweisung" aria-label="Projektzuweisung" @click="toggleActionDropdown('project')">
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.layer-group"></svg-vue>
                                    </button>
                                    <div
                                        v-show="actions.project"
                                        class="origin-top-right absolute top-full right-0 mt-2 w-72 rounded shadow-lg z-20"
                                        @click.stop
                                    >
                                        <div class="rounded bg-white shadow-xs">
                                            <div class="stage-icon-menu-header">Projektzuweisung</div>
                                            <div v-if="!ticket.recipient_user_id" class="px-4 py-2 text-sm text-gray-500">
                                                Kein Kunde zugewiesen.
                                            </div>
                                            <div v-else-if="availableProjects.length < 1" class="px-4 py-2 text-sm text-gray-500">
                                                Keine Projekte vorhanden.
                                            </div>
                                            <div v-else class="max-h-80 overflow-y-auto">
                                                <div class="py-1">
                                                    <template v-for="project in availableProjects">
                                                        <label class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer" @click.stop>
                                                            <input
                                                                v-model="selectedProjectIds"
                                                                :value="Number(project.id)"
                                                                class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                                type="checkbox"
                                                                @click.stop
                                                                @change="saveProjectAssignments"
                                                            >
                                                            <img
                                                                :src="project.image_url ? project.image_url : '/images/default/icon.png'"
                                                                :alt="project.title"
                                                                class="h-6 w-6 rounded-sm object-cover mr-2"
                                                            />
                                                            <span class="truncate">{{ project.title }}</span>
                                                        </label>
                                                    </template>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                    <div v-if="showApprovalManagementAction" class="relative inline-flex items-center">
                        <button class="btn-stage-icon-plain" type="button" title="Freigabeverwaltung" aria-label="Freigabeverwaltung" @click="openApprovalModal">
                            <svg-vue class="h-4 w-5" icon="font-awesome.regular.user-lock"></svg-vue>
                        </button>
                    </div>
                    <div v-if="isAdmin" class="relative inline-flex items-center">
                                    <button class="btn-stage-icon-plain" type="button" title="Optionen" aria-label="Optionen" @click="toggleActionDropdown('options')">
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                    </button>
                                    <div v-show="actions.options" class="origin-top-right absolute top-full right-0 mt-2 w-56 rounded shadow-lg z-20">
                                        <div class="rounded bg-white shadow-xs stage-dropdown-menu flex flex-col">
                                            <button
                                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                type="button"
                                                @click="openRenamePage"
                                            >
                                                Ticket umbenennen
                                            </button>
                                            <button
                                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                type="button"
                                                @click="openJsonPage"
                                            >
                                                JSON anzeigen
                                            </button>
                                            <button
                                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                type="button"
                                                @click="openXmlPage"
                                            >
                                                XML anzeigen
                                            </button>
                                            <button
                                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                type="button"
                                                @click="openDeleteTicketModal"
                                            >
                                                Ticket löschen
                                            </button>
                                        </div>
                                    </div>
                                </div>
                </div>
            </div>
        </div>

        <div class="stage-page-gutters stage-page-fill relative pt-3 h-0">
            <div class="w-full h-full bg-white shadow sm:rounded border border-ultralight overflow-hidden">
                <div class="flex items-stretch h-full">
            <div class="flex flex-col flex-auto min-w-0 min-h-0 h-full">
                <div class="ticket-conversation ticket-conversation--fill grid h-full min-h-0" style="grid-template-rows: auto minmax(0, 1fr);">
                    <div class="block min-h-0">
                            <div class="stage-ticket-detail-header-surface flex items-stretch min-h-14 border-b border-ultralight">
                                <div v-if="headerTicketProjects.length > 0" class="flex-shrink-0 border-r border-ultralight px-3 py-3 flex items-center">
                                    <div class="flex items-center flex-shrink-0">
                                        <template v-for="(project, projectIndex) in headerTicketProjects" :key="'ticket-header-project-' + Number(project.id) + '-' + projectIndex">
                                            <img
                                               
                                                :src="ticketProjectImageUrl(project)"
                                                :alt="project.title || 'Projektbild'"
                                                :title="projectTooltip(project)"
                                                :class="projectIndex === headerTicketProjects.length - 1 ? '' : '-mr-6'"
                                                class="h-8 w-8 rounded-sm object-cover border border-white"
                                            >
                                        </template>
                                    </div>
                                </div>
                                <div class="flex-1 min-w-0 px-4 py-3 flex items-center">
                                    <div class="text-lg font-semibold text-gray-900 text-left truncate whitespace-nowrap overflow-hidden text-ellipsis">
                                        {{ ticketHeaderTitle }}
                                    </div>
                                </div>
                                <div class="w-48 flex-shrink-0 border-l border-r border-ultralight">
                                    <select
                                        v-if="canManageStatus"
                                        id="ticket-status-header"
                                        v-model="headerStatusId"
                                        class="block form-select w-full h-full border-0 rounded-none bg-transparent pl-4 pr-9 py-0 text-sm leading-5 font-medium text-gray-700"
                                        @change="updateTicketStatus"
                                    >
                                        <template v-for="status in statusList" :key="'header-status-' + status.id">
                                            <option :value="status.id">{{ status.name }}</option>
                                        </template>
                                    </select>
                                    <div
                                        v-else
                                        class="h-full px-5 py-3 flex items-center text-sm leading-5 font-medium text-gray-700 whitespace-nowrap"
                                    >
                                        {{ headerStatusName }}
                                    </div>
                                </div>
                                <div v-if="canViewPriority" class="w-40 flex-shrink-0 border-r border-ultralight">
                                    <select
                                        id="ticket-priority-header"
                                        v-model="headerPriorityId"
                                        class="block form-select w-full h-full border-0 rounded-none bg-transparent pl-4 pr-9 py-0 text-sm leading-5 font-medium text-gray-700"
                                        @change="updateTicketPriority"
                                    >
                                        <template v-for="priority in priorityList" :key="'header-priority-' + priority.id">
                                            <option :value="priority.id">{{ priority.name }}</option>
                                        </template>
                                    </select>
                                </div>
                                <div class="w-28 flex-shrink-0">
                                    <button class="btn-stage-primary w-full h-full rounded-none" style="border-radius: 0;" type="button" @click="replyForm = true">
                                        Antworten
                                    </button>
                                </div>
                            </div>
                            <div v-if="canViewLabels && headerLabels.length > 0" class="overflow-x-auto overflow-y-hidden border-b border-ultralight bg-white px-5 py-3">
                                <div class="inline-flex min-w-max items-center gap-2">
                                    <template v-for="item in headerLabels" :key="'ticket-header-label-' + (item.label.id || item.index)">
                                        <div
                                            :style="{backgroundColor: item.label.color}"
                                            class="stage-ticket-badge"
                                        >
                                            <span class="stage-ticket-badge-label">{{ item.label.name }}</span>
                                            <button
                                                class="stage-ticket-badge-remove focus:outline-none focus:text-gray-100"
                                                type="button"
                                                @click="removeLabel(item.index)"
                                            >
                                                <svg-vue class="stage-ticket-badge-remove-icon" icon="font-awesome.times-solid"></svg-vue>
                                            </button>
                                        </div>
                                    </template>
                                </div>
                            </div>
                    </div>
                    <div class="flex-1 min-h-0 overflow-y-auto">
                        <div v-show="replyForm" class="px-6 pt-6 pb-6 border-b border-ultralight">
                            <loading :status="loading.reply"/>
                            <form @submit.prevent="addReply">
                                <input-wysiwyg
                                    id="ticket_body"
                                    v-model="ticketReply.body"
                                    :min-height="320"
                                    :cannedReplyList="canUseCannedReplies ? cannedReplyList : []"
                                    :plugins="{images: true, cannedReply: canUseCannedReplies, attachment: true, shortCode: true}"
                                    @selectUploadFile="selectUploadFile"
                                >
                                    <template v-slot:top>
                                        <div :class="{'bg-gray-200': uploadingFileProgress > 0}" class="h-1 w-full">
                                            <div :style="{width: uploadingFileProgress + '%'}" class="bg-blue-500 py-0.5"></div>
                                        </div>
                                    </template>
                                    <template v-slot:bottom>
                                        <div class="mt-3 flex items-center">
                                            <button
                                                :aria-label="$t('Discard')"
                                                :title="$t('Discard')"
                                                class="inline-flex items-center justify-center text-gray-400 focus:outline-none cursor-pointer mr-3"
                                                type="button"
                                                @click="discardReply"
                                            >
                                                <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                            </button>
                                            <div class="ml-auto flex">
                                                <button
                                                    class="btn btn-gray rounded-none"
                                                    type="submit"
                                                >
                                                    {{ $t('Send reply') }}
                                                </button>
                                            </div>
                                        </div>
                                    </template>
                                </input-wysiwyg>
                            </form>
                            <input ref="fileInput" hidden type="file" @change="uploadFile($event)">
                            <template v-if="ticketReply.attachments.length > 0">
                                <div class="mt-3 flex flex-wrap items-start gap-2">
                                    <template v-for="(attachment, index) in ticketReply.attachments">
                                        <attachment :details="attachment" v-on:remove="removeAttachment(index)"/>
                                    </template>
                                </div>
                            </template>
                        </div>
                        <template v-if="ticket.ticketReplies.length > 0">
                            <div class="block">
                                <template v-for="(ticketReply, index) in ticket.ticketReplies" :key="'ticket-reply-card-' + ticketReply.id + '-' + index">
                                    <div
                                       
                                        :class="index > 0 ? 'border-t border-ultralight' : ''"
                                        class="flex p-6"
                                    >
                                        <img
                                            :alt="$t('Avatar')"
                                            :src="ticketReply.user.avatar !== 'gravatar' ? ticketReply.user.avatar : ticketReply.user.gravatar"
                                            class="h-9 w-9 hidden sm:inline rounded-full object-cover"
                                        />
                                        <div class="sm:pl-6 pb-2 w-full">
                                            <div class="md:flex md:items-center pb-1">
                                                <div class="md:flex-1 text-lg font-semibold text-gray-800">
                                                    {{ ticketReply.user.name }}
                                                </div>
                                                <div class="md:flex-1">
                                                    <div class="md:float-right text-sm text-gray-500">
                                                        {{ $momentFormatDateTime(ticketReply.created_at) }}
                                                    </div>
                                                </div>
                                            </div>
                                            <p class="text-gray-600 font-base ticket-reply-body" v-html="ticketReply.body"/>
                                            <template v-if="ticketReply.attachments.length > 0">
                                                <div class="mt-5 flex flex-wrap items-start gap-2">
                                                    <template v-for="attachment in ticketReply.attachments">
                                                        <attachment :details="attachment" :remove-button="false"/>
                                                    </template>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </template>
                        <template v-else>
                            <empty-state
                                class="border-t border-ultralight"
                                :title="$t('This ticket has no conversations')"
                            />
                        </template>
                    </div>
                </div>
            </div>
            <div
                v-if="aiEnabled && aiSidebarOpen"
                :style="{flex: '0 0 352px', width: '352px'}"
                class="hidden lg:block border-l border-ultralight"
            >
                <div class="h-full py-4 px-6 overflow-y-auto bg-white">
                    <div class="flex items-center justify-between mb-3">
                        <div class="text-xl font-semibold text-gray-900">KI-Assistent</div>
                        <button class="text-gray-400 hover:text-gray-600" type="button" @click="aiSidebarOpen = false">
                            <svg-vue class="w-4 h-4" icon="font-awesome.times-light"></svg-vue>
                        </button>
                    </div>

                    <div class="relative min-h-[96px] mb-6">
                        <loading :status="aiLoading"/>
                        <div
                            v-if="!aiLoading"
                            class="text-sm leading-5 text-gray-600 whitespace-pre-line break-words"
                            style="hyphens: auto;"
                        >
                            {{ aiResult.reply || 'Noch keine Zusammenfassung vorhanden.' }}
                        </div>
                    </div>

                    <div v-if="!aiLoading && aiResult.actions.length > 0" class="mt-4 bg-white border border-l-4 border-l-orange-200 rounded overflow-hidden" style="border-color: var(--ui-border-subtle); border-left-color: #f97316;">
                        <div class="px-3 py-2 text-xs font-semibold text-gray-700 tracking-wide" style="background-color: var(--ui-surface-raised);">{{ $t('AI category actions') }}</div>
                        <div class="px-3">
                            <div
                                v-for="(item, index) in aiResult.actions"
                                :key="'detail-ai-action-' + index"
                                class="py-2 text-xs leading-5 text-gray-700"
                                :class="index === 0 ? '' : 'border-t'"
                                style="border-color: var(--ui-border-subtle);"
                            >
                                {{ item }}
                            </div>
                        </div>
                    </div>

                    <div v-if="!aiLoading && aiResult.alerts.length > 0" class="mt-4 bg-white border border-l-4 border-l-red-200 rounded overflow-hidden" style="border-color: var(--ui-border-subtle); border-left-color: #ef4444;">
                        <div class="px-3 py-2 text-xs font-semibold text-gray-700 tracking-wide" style="background-color: var(--ui-surface-raised);">{{ $t('AI category alerts') }}</div>
                        <div class="px-3">
                            <div
                                v-for="(item, index) in aiResult.alerts"
                                :key="'detail-ai-alert-' + index"
                                class="py-2 text-xs leading-5 text-gray-700"
                                :class="index === 0 ? '' : 'border-t'"
                                style="border-color: var(--ui-border-subtle);"
                            >
                                {{ item }}
                            </div>
                        </div>
                    </div>

                    <div v-if="!aiLoading && aiResult.warnings.length > 0" class="mt-4 bg-white border border-l-4 border-l-yellow-200 rounded overflow-hidden" style="border-color: var(--ui-border-subtle); border-left-color: #eab308;">
                        <div class="px-3 py-2 text-xs font-semibold text-gray-700 tracking-wide" style="background-color: var(--ui-surface-raised);">{{ $t('AI category warnings') }}</div>
                        <div class="px-3">
                            <div
                                v-for="(item, index) in aiResult.warnings"
                                :key="'detail-ai-warning-' + index"
                                class="py-2 text-xs leading-5 text-gray-700"
                                :class="index === 0 ? '' : 'border-t'"
                                style="border-color: var(--ui-border-subtle);"
                            >
                                {{ item }}
                            </div>
                        </div>
                    </div>

                    <div v-if="!aiLoading && aiResult.notes.length > 0" class="mt-4 bg-white border border-l-4 border-l-blue-200 rounded overflow-hidden" style="border-color: var(--ui-border-subtle); border-left-color: #3b82f6;">
                        <div class="px-3 py-2 text-xs font-semibold text-gray-700 tracking-wide" style="background-color: var(--ui-surface-raised);">{{ $t('AI category notes') }}</div>
                        <div class="px-3">
                            <div
                                v-for="(item, index) in aiResult.notes"
                                :key="'detail-ai-note-' + index"
                                class="py-2 text-xs leading-5 text-gray-700"
                                :class="index === 0 ? '' : 'border-t'"
                                style="border-color: var(--ui-border-subtle);"
                            >
                                {{ item }}
                            </div>
                        </div>
                    </div>

                    <div v-if="!aiLoading && aiResult.links.length > 0" class="mt-4 bg-white border rounded overflow-hidden" style="border-color: var(--ui-border-subtle);">
                        <div class="px-3">
                            <div
                                v-for="(link, index) in aiResult.links"
                                :key="'detail-ai-link-' + index"
                                class="flex items-start gap-2 py-2"
                                :class="index === 0 ? '' : 'border-t'"
                                style="border-color: var(--ui-border-subtle);"
                            >
                                <span class="text-gray-500 leading-5 flex-shrink-0" aria-hidden="true">
                                    <svg class="w-3 h-3 mt-1" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H17a4 4 0 0 1 0 8h-3.5m-3-4H7a4 4 0 0 0 0 8h3.5m-2-8h7" />
                                    </svg>
                                </span>
                                <a
                                    :href="link.url"
                                    class="text-xs leading-5 text-gray-700 hover:text-gray-900 break-all"
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    {{ link.label }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div
                :style="{flex: '0 0 352px', width: '352px'}"
                class="hidden lg:block border-l border-ultralight"
            >
                <div>
                <div v-if="primarySidebarUser" class="mx-6 pt-6 pb-6 mb-6 border-b border-ultralight">
                    <div class="flex items-start">
                        <img
                            :alt="$t('Avatar')"
                            :src="primarySidebarUser.avatar !== 'gravatar' ? primarySidebarUser.avatar : primarySidebarUser.gravatar"
                            class="h-14 w-14 rounded-full object-cover"
                        />
                    </div>
                    <div class="mt-2 min-w-0">
                        <div class="mb-2 font-semibold text-base text-gray-800 truncate">{{ primarySidebarUser.name || '-' }}</div>
                        <div class="mb-1 text-sm leading-4 text-gray-500 truncate">
                            {{ primarySidebarUser.email || '-' }}
                        </div>
                        <div v-if="getUserPhone(primarySidebarUser)" class="text-sm leading-4 text-gray-500 truncate">
                            {{ getUserPhone(primarySidebarUser) }}
                        </div>
                    </div>
                </div>
                <div v-if="isExternalViewer && externalRecipientSidebarUsers.length > 0" class="px-6 pb-6">
                    <div class="text-xs tracking-wide text-gray-500 mb-2">
                        Empfänger
                    </div>
                    <div class="space-y-4">
                        <template v-for="person in externalRecipientSidebarUsers" :key="'sidebar-external-recipient-user-' + person.id">
                            <div class="flex items-start">
                                <div class="mr-3 flex-shrink-0">
                                    <img
                                        :alt="$t('Avatar')"
                                        :src="person.avatar !== 'gravatar' ? person.avatar : person.gravatar"
                                        class="h-9 w-9 rounded-full object-cover"
                                    />
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="text-gray-800 font-regular text-sm truncate">{{ person.name }}</div>
                                    <div class="text-sm leading-4 text-gray-500 truncate">
                                        {{ person.email || '-' }}
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
                <div v-if="isExternalViewer && externalSharedSidebarUsers.length > 0" class="px-6 pb-6">
                    <div class="text-xs tracking-wide text-gray-500 mb-2">
                        Geteilt
                    </div>
                    <div class="space-y-4">
                        <template v-for="person in externalSharedSidebarUsers" :key="'sidebar-external-shared-user-' + person.id">
                            <div class="flex items-start">
                                <div class="mr-3 flex-shrink-0">
                                    <img
                                        :alt="$t('Avatar')"
                                        :src="person.avatar !== 'gravatar' ? person.avatar : person.gravatar"
                                        class="h-9 w-9 rounded-full object-cover"
                                    />
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="text-gray-800 font-regular text-sm truncate">{{ person.name }}</div>
                                    <div class="text-sm leading-4 text-gray-500 truncate">
                                        {{ person.email || '-' }}
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
                <div v-if="!isGuest && senderSidebarUsers.length > 0" class="px-6 pb-6">
                    <div class="text-xs tracking-wide text-gray-500 mb-2">
                        Sender
                    </div>
                    <div class="space-y-4">
                        <template v-for="person in senderSidebarUsers" :key="'sidebar-sender-user-' + person.id">
                            <div class="flex items-start">
                                <div class="mr-3 flex-shrink-0">
                                    <img
                                        :alt="$t('Avatar')"
                                        :src="person.avatar !== 'gravatar' ? person.avatar : person.gravatar"
                                        class="h-9 w-9 rounded-full object-cover"
                                    />
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="text-gray-800 font-regular text-sm truncate">{{ person.name }}</div>
                                    <div class="text-sm leading-4 text-gray-500 truncate">
                                        {{ person.email || '-' }}
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
                <div v-if="showInternalReleaseSidebar" class="px-6 pb-6">
                    <div class="text-xs tracking-wide text-gray-500 mb-2">
                        {{ $t('Internal release') }}
                    </div>
                    <div class="space-y-4">
                        <template v-for="person in internalApprovalUsers" :key="'sidebar-internal-approval-user-' + person.id">
                            <div class="flex items-start">
                                <div class="mr-3 flex-shrink-0">
                                    <img
                                        :alt="$t('Avatar')"
                                        :src="person.avatar !== 'gravatar' ? person.avatar : person.gravatar"
                                        class="h-9 w-9 rounded-full object-cover"
                                    />
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="text-gray-800 font-regular text-sm truncate">{{ person.name }}</div>
                                    <div class="text-sm leading-4 text-gray-500 truncate">
                                        {{ person.email || '-' }}
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
                <div v-if="!isGuest && customerApprovalUsers.length > 0" class="px-6 pb-6">
                    <div class="text-xs tracking-wide text-gray-500 mb-2">
                        {{ $t('Customer release') }}
                    </div>
                    <div class="space-y-4">
                        <template v-for="person in customerApprovalUsers" :key="'sidebar-customer-approval-user-' + person.id">
                            <div class="flex items-start">
                                <div class="mr-3 flex-shrink-0">
                                    <img
                                        :alt="$t('Avatar')"
                                        :src="person.avatar !== 'gravatar' ? person.avatar : person.gravatar"
                                        class="h-9 w-9 rounded-full object-cover"
                                    />
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="text-gray-800 font-regular text-sm truncate">{{ person.name }}</div>
                                    <div class="text-sm leading-4 text-gray-500 truncate">
                                        {{ person.email || '-' }}
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
                <div v-if="!isGuest && guestApprovalUsers.length > 0" class="px-6 pb-6">
                    <div class="text-xs tracking-wide text-gray-500 mb-2">
                        {{ $t('Guest release') }}
                    </div>
                    <div class="space-y-4">
                        <template v-for="person in guestApprovalUsers" :key="'sidebar-guest-approval-user-' + person.id">
                            <div class="flex items-start">
                                <div class="mr-3 flex-shrink-0">
                                    <img
                                        :alt="$t('Avatar')"
                                        :src="person.avatar !== 'gravatar' ? person.avatar : person.gravatar"
                                        class="h-9 w-9 rounded-full object-cover"
                                    />
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="text-gray-800 font-regular text-sm truncate">{{ person.name }}</div>
                                    <div class="text-sm leading-4 text-gray-500 truncate">
                                        {{ person.email || '-' }}
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

                </div>
            </div>
                </div>
            </div>
        </div>
        <confirm-dialog :open="deleteTicketModal" :title="$t('Delete ticket').toString()" :text="`${$t('Are you sure you want to delete the ticket?')} ${$t('All data will be permanently removed')}. ${$t('All related data will be deleted')}. ${$t('This action cannot be undone')}.`" :confirm-label="$t('Delete ticket').toString()" confirm-variant="danger" @close="deleteTicketModal = false" @confirm="deleteTicket" />
        <approval-management-dialog
            :open="approvalModal"
            :title="approvalModalTitle"
            :show-tab-bar="showApprovalModalTabs"
            :internal-users="filteredAgentList"
            :customer-users="availableCustomers"
            :guest-users="availableGuests"
            :selected-internal-ids="selectedAgentIds"
            :selected-customer-ids="selectedCustomerIds"
            :selected-guest-ids="selectedGuestIds"
            :show-customer-tab="isAdmin"
            :show-guest-tab="isAdmin"
            @close="closeApprovalModal"
            @save="saveApprovalSelections"
        />
    </div>
</template>

<script>
import {mixin as clickaway} from "vue-clickaway";
import ApprovalManagementDialog from "@/components/modals/approval-management-dialog";
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {resolveAccessState} from "@/utils/access-state";
import {resolveReturnToPath} from "@/utils/list-route-state";
import {refreshPwaBadgeCount} from "@/utils/pwa-badge";
import {
    canManageTicketLabels,
    canManageTicketStatus,
    canManageTicketTopic,
    canUseTicketCannedReplies,
    canViewTicketLabels,
    canViewTicketPriority,
    isExternalTicketViewer,
    showTicketApprovalTabs,
} from "@/utils/ticket-access";

export default {
    name: "manage",
    components: {
        ApprovalManagementDialog,
        ConfirmDialog,
    },
    mixins: [clickaway],
    metaInfo() {
        return {
            title: this.$i18n.t('Manage ticket')
        }
    },
    mounted() {
        this.getTicket();
        this.getFilters();
        if (this.canUseCannedReplies) {
            this.getCannedReplies();
        }
    },
    data() {
        return {
            loading: {
                form: true,
                reply: false,
                file: false,
            },
            deleteTicketModal: false,
            approvalModal: false,
            replyForm: false,
            uploadingFileProgress: 0,
            ticket: {
                subject: null,
                created_at: null,
                labels: [],
                agents: [],
                ticketReplies: [],
            },
            ticketReply: {
                status_id: null,
                body: '',
                attachments: [],
            },
            headerStatusId: null,
            headerPriorityId: null,
            actions: {
                agent: false,
                ticketTopic: false,
                customer: false,
                project: false,
                label: false,
                priority: false,
                options: false,
            },
            cannedReplyList: [],
            agentList: [],
            selectedAgentIds: [],
            customerList: [],
            selectedCustomerIds: [],
            guestList: [],
            selectedGuestIds: [],
            selectedProjectIds: [],
            selectedTicketTopicId: null,
            selectedLabelId: null,
            ticketTopicList: [],
            labelList: [],
            statusList: [],
            priorityList: [],
            aiEnabled: false,
            aiSidebarOpen: false,
            aiLoading: false,
            aiResult: {
                reply: '',
                actions: [],
                alerts: [],
                warnings: [],
                notes: [],
                links: [],
            },
        }
    },
    computed: {
        currentUser() {
            return this.$store && this.$store.state ? this.$store.state.user : null;
        },
        accessState() {
            return resolveAccessState(this.$store);
        },
        isAdmin() {
            return this.accessState.isAdmin;
        },
        isCustomerTicketRoute() {
            return String(this.$route.path || '').startsWith('/tickets');
        },
        ticketListPath() {
            const fallbackPath = this.isCustomerTicketRoute ? '/tickets/list' : '/workspace/tickets';
            return resolveReturnToPath(this.$route.query || {}, fallbackPath);
        },
        isServiceProvider() {
            return this.accessState.isServiceProvider;
        },
        isGuest() {
            return this.accessState.isGuest;
        },
        canUseCannedReplies() {
            return canUseTicketCannedReplies(this.accessState);
        },
        isExternalViewer() {
            return isExternalTicketViewer(this.accessState);
        },
        canManageStatus() {
            return canManageTicketStatus(this.accessState);
        },
        canManageTicketTopic() {
            return canManageTicketTopic(this.accessState);
        },
        canManageLabels() {
            return canManageTicketLabels(this.accessState);
        },
        canViewLabels() {
            return canViewTicketLabels(this.accessState);
        },
        canViewPriority() {
            return canViewTicketPriority(this.accessState);
        },
        showApprovalModalTabs() {
            return showTicketApprovalTabs(this.accessState);
        },
        approvalModalTitle() {
            return this.showApprovalModalTabs ? 'Freigabeverwaltung' : 'Geteilt';
        },
        showInternalReleaseSidebar() {
            return !isExternalTicketViewer(this.accessState)
                && canUseTicketCannedReplies(this.accessState)
                && this.internalApprovalUsers.length > 0;
        },
        headerStatusName() {
            const statusId = Number(this.headerStatusId || this.ticket?.status_id || 0);
            const selectedStatus = (this.statusList || []).find((status) => Number(status?.id || 0) === statusId);
            if (selectedStatus && String(selectedStatus.name || '').trim() !== '') {
                return selectedStatus.name;
            }

            return String(this.ticket?.status?.name || '').trim() || '-';
        },
        filteredAgentList() {
            const agents = Array.isArray(this.agentList) ? this.agentList : [];
            if (!this.isServiceProvider) {
                return agents;
            }

            const currentUserId = Number((this.currentUser && this.currentUser.id) || 0);
            return agents.filter((agent) => {
                if (!agent) {
                    return false;
                }

                return Number(agent.id) !== currentUserId;
            });
        },
        showInternalApprovalAction() {
            if (this.isGuest) {
                return false;
            }

            if (this.isAdmin) {
                return true;
            }

            if (!this.isServiceProvider) {
                return true;
            }

            return Boolean(this.ticket && this.ticket.can_manage_internal_approval);
        },
        showApprovalManagementAction() {
            if (this.isGuest) {
                return false;
            }

            return this.showInternalApprovalAction || this.isAdmin;
        },
        ticketHeaderTitle() {
            const subject = String(this.ticket && this.ticket.subject ? this.ticket.subject : '').trim();
            return subject !== '' ? subject : '-';
        },
        headerLabels() {
            return Array.isArray(this.ticket && this.ticket.labels)
                ? this.ticket.labels
                    .map((label, index) => ({label, index}))
                    .filter((item) => item.label && String(item.label.name || '').trim() !== '')
                : [];
        },
        customerApprovalUsers() {
            const unique = [];
            const seen = new Set();
            const excludedIds = new Set([
                Number(this.ticket.user_id || 0),
                Number(this.primarySidebarUser?.id || 0),
            ]);

            (this.ticket.customers || []).forEach((person) => {
                const id = Number(person && person.id ? person.id : 0);
                if (!id || excludedIds.has(id) || seen.has(id)) {
                    return;
                }

                seen.add(id);
                unique.push(person);
            });

            return unique;
        },
        primarySidebarUser() {
            if (this.isExternalViewer) {
                return this.ticket?.sender || this.ticket?.creator || this.ticket?.recipient || this.ticket?.user || null;
            }

            return this.ticket?.recipient || this.ticket?.user || this.ticket?.creator || null;
        },
        senderSidebarUsers() {
            if (this.isGuest) {
                return [];
            }

            const sender = this.ticket?.sender || this.ticket?.creator || null;
            const senderId = Number(sender?.id || 0);
            const primaryId = Number(this.primarySidebarUser?.id || 0);

            if (!senderId || senderId === primaryId) {
                return [];
            }

            return [sender];
        },
        externalRecipientSidebarUsers() {
            if (!this.isExternalViewer) {
                return [];
            }

            const recipient = this.ticket?.recipient || this.ticket?.user || null;
            const recipientId = Number(recipient?.id || 0);
            const primaryId = Number(this.primarySidebarUser?.id || 0);

            if (!recipientId || recipientId === primaryId) {
                return [];
            }

            return [recipient];
        },
        externalSharedSidebarUsers() {
            if (!this.isExternalViewer) {
                return [];
            }

            const shared = [];
            const seen = new Set();
            const excludedIds = new Set([
                Number(this.primarySidebarUser?.id || 0),
                ...this.externalRecipientSidebarUsers.map((person) => Number(person?.id || 0)),
            ]);

            [
                ...this.internalApprovalUsers,
                ...this.customerApprovalUsers,
                ...this.guestApprovalUsers,
            ].forEach((person) => {
                const id = Number(person && person.id ? person.id : 0);
                if (!id || excludedIds.has(id) || seen.has(id)) {
                    return;
                }

                seen.add(id);
                shared.push(person);
            });

            return shared;
        },
        internalApprovalUsers() {
            const people = [this.ticket.agent, ...(this.ticket.agents || [])];
            const unique = [];
            const seen = new Set();
            const excludedIds = new Set([
                Number(this.ticket.user_id || 0),
            ]);

            people.forEach((person) => {
                const id = Number(person && person.id ? person.id : 0);
                if (!id || seen.has(id) || excludedIds.has(id)) {
                    return;
                }

                seen.add(id);
                unique.push(person);
            });

            return unique;
        },
        guestApprovalUsers() {
            if (!Array.isArray(this.ticket?.guests)) {
                return [];
            }

            const unique = [];
            const seen = new Set();
            const excludedIds = new Set([
                Number(this.primarySidebarUser?.id || 0),
            ]);

            (this.ticket.guests || []).forEach((person) => {
                const id = Number(person && person.id ? person.id : 0);
                if (!id || seen.has(id) || excludedIds.has(id)) {
                    return;
                }

                seen.add(id);
                unique.push(person);
            });

            return unique;
        },
        availableCustomers() {
            return (this.customerList || []).filter((customer) => Number(customer.id) !== Number(this.ticket.user_id));
        },
        availableGuests() {
            return this.guestList || [];
        },
        availableProjects() {
            const collection = this.ticket && this.ticket.available_projects ? this.ticket.available_projects : [];
            if (Array.isArray(collection)) {
                return collection;
            }
            if (collection && Array.isArray(collection.data)) {
                return collection.data;
            }
            return [];
        },
        headerTicketProjects() {
            const projects = Array.isArray(this.ticket?.projects)
                ? this.ticket.projects
                : (this.ticket?.projects && Array.isArray(this.ticket.projects.data) ? this.ticket.projects.data : []);
            const normalizedProjects = projects.filter((project) => project && Number(project.id) > 0);
            if (normalizedProjects.length > 0) {
                return normalizedProjects;
            }

            if (this.ticket?.project && Number(this.ticket.project.id) > 0) {
                return [this.ticket.project];
            }

            const selectedIds = this.normalizeProjectIds(this.ticket);
            if (selectedIds.length < 1) {
                return [];
            }

            const availableById = new Map(
                (this.availableProjects || [])
                    .filter((project) => project && Number(project.id) > 0)
                    .map((project) => [Number(project.id), project])
            );

            return selectedIds
                .map((projectId) => availableById.get(Number(projectId)))
                .filter((project) => !!project);
        },
    },
    filters: {
        momentFormatDateTime: function (value) {
            return moment(value).locale(window.app.app_date_locale).format(window.app.app_date_format + ' HH:mm');
        },
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    methods: {
        ticketManagePath(ticketId) {
            return this.isCustomerTicketRoute
                ? '/tickets/' + ticketId
                : '/workspace/tickets/' + ticketId + '/manage';
        },
        ticketEditPath(ticketId) {
            return this.isCustomerTicketRoute
                ? '/tickets/' + ticketId + '/edit'
                : '/workspace/tickets/' + ticketId + '/edit';
        },
        ticketJsonPath(ticketId) {
            return '/workspace/tickets/' + ticketId + '/json';
        },
        ticketXmlPath(ticketId) {
            return '/workspace/tickets/' + ticketId + '/xml';
        },
        normalizeAiEnabled(value) {
            return value === true || value === 1 || value === '1';
        },
        applyTicketState(ticket) {
            this.ticket = ticket;
            this.ticketReply.status_id = ticket.status_id;
            this.headerStatusId = ticket.status_id;
            this.headerPriorityId = ticket.priority_id || (ticket.priority ? Number(ticket.priority.id) : null);
            this.selectedTicketTopicId = ticket.ticket_topic_id;
            this.syncProjectSelection(ticket);
            this.selectedLabelId = ticket.labels && ticket.labels.length ? Number(ticket.labels[0].id) : null;
        },
        toggleAiSidebar() {
            if (!this.aiEnabled) {
                return;
            }

            // Keep header dropdown state consistent when switching to AI sidebar.
            this.closeActionDropdown();
            this.aiSidebarOpen = !this.aiSidebarOpen;
            if (this.aiSidebarOpen) {
                this.runAiSummary();
            }
        },
        getTicket() {
            const self = this;
            self.loading.form = true;
            axios.get('api/workspace/tickets/' + self.$route.params.id).then(function (response) {
                self.loading.form = false;
                self.ticket = response.data;
                self.ticketReply.status_id = response.data.status_id;
                self.headerStatusId = response.data.status_id;
                self.headerPriorityId = response.data.priority_id || (response.data.priority ? Number(response.data.priority.id) : null);
                self.selectedTicketTopicId = response.data.ticket_topic_id;
                self.selectedAgentIds = (response.data.agents || []).map(function (agent) {
                    return Number(agent.id);
                });
                if (self.selectedAgentIds.length === 0 && response.data.agent_id) {
                    self.selectedAgentIds = [Number(response.data.agent_id)];
                }
                self.selectedCustomerIds = (response.data.customers || []).map(function (customer) {
                    return Number(customer.id);
                }).filter(function (id) {
                    return id !== Number(response.data.user_id);
                });
                self.selectedGuestIds = (response.data.guests || []).map(function (guest) {
                    return Number(guest.id);
                });
                self.syncProjectSelection(response.data);
                self.selectedLabelId = response.data.labels && response.data.labels.length ? Number(response.data.labels[0].id) : null;
                self.syncHeaderPrioritySelection();
                self.markTicketAsRead();
            }).catch(function () {
                self.$router.push(self.ticketListPath);
            });
        },
        markTicketAsRead() {
            if (!this.ticket?.id) {
                return;
            }

            axios.post('api/workspace/tickets/' + this.ticket.id + '/mark-read').then(function () {
                refreshPwaBadgeCount().catch(function () {
                });
            }).catch(function () {});
        },
        getCannedReplies() {
            if (!this.canUseCannedReplies) {
                this.cannedReplyList = [];
                return;
            }

            const self = this;
            axios.get('api/workspace/tickets/canned-replies').then(function (response) {
                self.cannedReplyList = response.data;
            }).catch(function () {
                self.cannedReplyList = [];
            });
        },
        getFilters() {
            const self = this;
            axios.get('api/workspace/tickets/filters', {
                params: {
                    ticket_id: self.$route.params.id,
                },
            }).then(function (response) {
                self.agentList = self.normalizeCollection(response.data.agents);
                self.customerList = self.normalizeCollection(response.data.customers);
                self.guestList = self.normalizeCollection(response.data.guests || response.data.guest_users);
                self.ticketTopicList = self.normalizeCollection(response.data.ticket_topics);
                self.labelList = self.normalizeCollection(response.data.labels);
                self.statusList = self.normalizeCollection(response.data.statuses);
                self.priorityList = self.normalizeCollection(response.data.priorities);
                self.aiEnabled = self.normalizeAiEnabled(response.data.ai_enabled);
                if (!self.aiEnabled) {
                    self.aiSidebarOpen = false;
                }

                self.syncHeaderPrioritySelection();
            });
        },
        syncHeaderPrioritySelection() {
            if (!Array.isArray(this.priorityList) || this.priorityList.length === 0) {
                return;
            }

            const currentPriorityId = Number(this.headerPriorityId || 0);
            const hasPriority = this.priorityList.some(function (priority) {
                return Number(priority.id) === currentPriorityId;
            });

            if (!hasPriority) {
                this.headerPriorityId = Number(this.priorityList[0].id);
            }
        },
        normalizeCollection(collection) {
            if (Array.isArray(collection)) {
                return collection;
            }
            if (collection && Array.isArray(collection.data)) {
                return collection.data;
            }
            return [];
        },
        normalizeProjectIds(source) {
            const list = Array.isArray(source?.project_ids)
                ? source.project_ids
                : (source?.project_ids && Array.isArray(source.project_ids.data) ? source.project_ids.data : []);
            if (list.length > 0) {
                return list
                    .map(function (id) {
                        return Number(id);
                    })
                    .filter(function (id) {
                        return id > 0;
                    });
            }

            const fallbackProjectId = Number(source?.project_id || 0);
            return fallbackProjectId > 0 ? [fallbackProjectId] : [];
        },
        ticketProjectImageUrl(project) {
            if (project && project.image_url) {
                return project.image_url;
            }

            return '/images/default/icon.png';
        },
        projectTooltip(project) {
            const title = String(project?.title || '').trim();
            return title !== '' ? `Projekt: ${title}` : 'Projekt';
        },
        isLabelAssigned(labelId) {
            const targetId = Number(labelId || 0);
            return Array.isArray(this.ticket && this.ticket.labels)
                && this.ticket.labels.some((label) => Number(label && label.id ? label.id : 0) === targetId);
        },
        toggleTicketLabel(label) {
            if (!this.canManageLabels) {
                return;
            }

            const labelId = Number(label && label.id ? label.id : 0);
            if (!labelId) {
                return;
            }

            if (this.isLabelAssigned(labelId)) {
                const existingIndex = (this.ticket.labels || []).findIndex((item) => Number(item && item.id ? item.id : 0) === labelId);
                if (existingIndex >= 0) {
                    this.removeLabel(existingIndex, {preserveDropdown: true});
                }
                return;
            }

            const self = this;
            axios.post('api/workspace/tickets/' + self.$route.params.id + '/quick-actions', {
                action: 'label',
                value: labelId,
            }).then(function (response) {
                if (!response.data.access) {
                    self.$router.push(self.ticketListPath);
                    return;
                }

                self.applyTicketState(response.data.ticket);
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        syncProjectSelection(source) {
            this.selectedProjectIds = this.normalizeProjectIds(source);
        },
        normalizeAiStringList(value) {
            const values = Array.isArray(value) ? value : [value];
            return values
                .map((entry) => String(entry || '').trim())
                .filter((entry) => entry !== '');
        },
        normalizeAiLinks(value) {
            const values = Array.isArray(value) ? value : [];
            return values.map((entry) => {
                if (entry && typeof entry === 'object') {
                    const label = String(entry.label || '').trim();
                    const url = this.normalizeAiLinkUrl(String(entry.url || '').trim());
                    return {
                        label: label || url,
                        url: url || '#',
                    };
                }
                const raw = this.normalizeAiLinkUrl(String(entry || '').trim());
                return {
                    label: raw,
                    url: raw || '#',
                };
            }).filter((entry) => entry.url && entry.url !== '#');
        },
        normalizeAiLinkUrl(rawUrl) {
            const url = String(rawUrl || '').trim();
            if (url === '') {
                return '';
            }

            const lower = url.toLowerCase();
            const isFileApiLink = lower.includes('/api/files/download/') || lower.includes('/api/files/preview/');
            if (!isFileApiLink) {
                return url;
            }

            if (lower.includes('token=')) {
                return url;
            }

            const token = typeof getAuthToken === 'function' ? getAuthToken() : '';
            if (!token) {
                return url;
            }

            return url + (url.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(token);
        },
        runAiSummary() {
            if (this.aiLoading || !this.ticket || !this.ticket.id) {
                return;
            }

            this.aiLoading = true;

            axios.post('api/workspace/tickets/ai-assistant', {
                ticket_id: Number(this.ticket.id),
            }).then((response) => {
                this.aiResult = {
                    reply: String(response.data?.reply || response.data?.summary || response.data?.answer || 'Es konnte keine Zusammenfassung erstellt werden.'),
                    actions: this.normalizeAiStringList(response.data?.actions || response.data?.action_items),
                    alerts: this.normalizeAiStringList(response.data?.alerts),
                    warnings: this.normalizeAiStringList(response.data?.warnings),
                    notes: this.normalizeAiStringList(response.data?.notes),
                    links: this.normalizeAiLinks(response.data?.links),
                };
            }).catch((error) => {
                const message = String(error?.response?.data?.message || 'Die Anfrage konnte nicht verarbeitet werden.');
                this.aiResult = {
                    reply: 'Fehler: ' + message,
                    actions: [],
                    alerts: [],
                    warnings: [],
                    notes: [],
                    links: [],
                };
            }).finally(() => {
                this.aiLoading = false;
            });
        },
        discardReply() {
            this.ticketReply.body = '';
            this.ticketReply.attachments = [];
            this.replyForm = false;
        },
        openApprovalModal() {
            this.closeActionDropdown();
            this.approvalModal = true;
        },
        closeApprovalModal() {
            this.approvalModal = false;
        },
        selectUploadFile() {
            if (!this.loading.file) {
                this.$refs.fileInput.click();
            } else {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('A file is being uploaded').toString(),
                    type: 'warning'
                });
            }
        },
        uploadFile(e) {
            const self = this;
            const formData = new FormData();
            self.loading.file = true;
            formData.append('file', e.target.files[0]);
            axios.post(
                'api/workspace/tickets/attachments',
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: function (progressEvent) {
                        self.uploadingFileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    }.bind(this)
                }
            ).then(function (response) {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
                self.ticketReply.attachments.push(response.data);
            }).catch(function () {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
            });
        },
        removeAttachment(attachment) {
            this.ticketReply.attachments.splice(attachment, 1);
        },
        removeLabel(index, options = {}) {
            const self = this;
            if (!self.canManageLabels) {
                return;
            }

            const label = self.ticket.labels[index];
            if (!label || !label.id) {
                return;
            }
            axios.post('api/workspace/tickets/' + self.$route.params.id + '/remove-label', {
                label: label.id,
            }).then(function () {
                if (options && options.preserveDropdown !== true) {
                    self.actions.label = false;
                }
                self.ticket.labels.splice(index, 1);
                self.selectedLabelId = self.ticket.labels.length ? Number(self.ticket.labels[0].id) : null;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            });
        },
        addReply() {
            const self = this;
            self.loading.reply = true;
            axios.post('api/workspace/tickets/' + self.$route.params.id + '/reply', self.ticketReply).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.ticket = response.data.ticket;
                self.ticketReply.status_id = response.data.ticket.status_id;
                self.headerStatusId = response.data.ticket.status_id;
                self.headerPriorityId = response.data.ticket.priority_id;
                self.selectedTicketTopicId = response.data.ticket.ticket_topic_id;
                self.syncProjectSelection(response.data.ticket);
                self.selectedLabelId = response.data.ticket.labels && response.data.ticket.labels.length ? Number(response.data.ticket.labels[0].id) : null;
                self.discardReply();
                self.loading.reply = false;
            }).catch(function () {
                self.loading.reply = false;
            });
        },
        closeActionDropdown() {
            this.actions.agent = false;
            this.actions.ticketTopic = false;
            this.actions.customer = false;
            this.actions.project = false;
            this.actions.label = false;
            this.actions.priority = false;
            this.actions.options = false;
        },
        toggleActionDropdown(action) {
            if (action === 'agent') {
                this.actions.agent = !this.actions.agent;
                this.actions.ticketTopic = false;
                this.actions.customer = false;
                this.actions.project = false;
                this.actions.label = false;
                this.actions.priority = false;
                this.actions.options = false;
            }
            if (action === 'ticketTopic') {
                this.actions.ticketTopic = !this.actions.ticketTopic;
                this.actions.agent = false;
                this.actions.customer = false;
                this.actions.project = false;
                this.actions.label = false;
                this.actions.priority = false;
                this.actions.options = false;
            }
            if (action === 'customer') {
                this.actions.customer = !this.actions.customer;
                this.actions.agent = false;
                this.actions.ticketTopic = false;
                this.actions.project = false;
                this.actions.label = false;
                this.actions.priority = false;
                this.actions.options = false;
            }
            if (action === 'project') {
                this.actions.project = !this.actions.project;
                this.actions.agent = false;
                this.actions.ticketTopic = false;
                this.actions.customer = false;
                this.actions.label = false;
                this.actions.priority = false;
                this.actions.options = false;
            }
            if (action === 'label') {
                this.actions.label = !this.actions.label;
                this.actions.agent = false;
                this.actions.ticketTopic = false;
                this.actions.customer = false;
                this.actions.project = false;
                this.actions.priority = false;
                this.actions.options = false;
            }
            if (action === 'priority') {
                this.actions.priority = !this.actions.priority;
                this.actions.agent = false;
                this.actions.ticketTopic = false;
                this.actions.customer = false;
                this.actions.project = false;
                this.actions.label = false;
                this.actions.options = false;
            }
            if (action === 'options') {
                this.actions.options = !this.actions.options;
                this.actions.agent = false;
                this.actions.ticketTopic = false;
                this.actions.customer = false;
                this.actions.project = false;
                this.actions.label = false;
                this.actions.priority = false;
            }
        },
        openRenamePage() {
            this.closeActionDropdown();
            this.$router.push(this.ticketEditPath(this.$route.params.id));
        },
        openJsonPage() {
            this.closeActionDropdown();
            this.$router.push(this.ticketJsonPath(this.$route.params.id));
        },
        openXmlPage() {
            this.closeActionDropdown();
            this.$router.push(this.ticketXmlPath(this.$route.params.id));
        },
        openDeleteTicketModal() {
            this.closeActionDropdown();
            this.deleteTicketModal = true;
        },
        action(param, value) {
            const self = this;
            axios.post('api/workspace/tickets/' + self.$route.params.id + '/quick-actions', {
                action: param,
                value: value,
            }).then(function (response) {
                self.closeActionDropdown();
                if (!response.data.access) {
                    self.$router.push(self.ticketListPath);
                } else {
                    self.ticket = response.data.ticket;
                    self.ticketReply.status_id = response.data.ticket.status_id;
                    self.headerStatusId = response.data.ticket.status_id;
                    self.headerPriorityId = response.data.ticket.priority_id;
                    self.selectedTicketTopicId = response.data.ticket.ticket_topic_id;
                    self.syncProjectSelection(response.data.ticket);
                    self.selectedLabelId = response.data.ticket.labels && response.data.ticket.labels.length ? Number(response.data.ticket.labels[0].id) : null;
                    self.selectedAgentIds = (response.data.ticket.agents || []).map(function (agent) {
                        return Number(agent.id);
                    });
                    if (self.selectedAgentIds.length === 0 && response.data.ticket.agent_id) {
                        self.selectedAgentIds = [Number(response.data.ticket.agent_id)];
                    }
                    self.$notify({
                        title: self.$i18n.t('Success').toString(),
                        text: self.$i18n.t('Data saved correctly').toString(),
                        type: 'success'
                    });
                }
            }).catch(function () {
                self.selectedLabelId = self.ticket.labels && self.ticket.labels.length ? Number(self.ticket.labels[0].id) : null;
                self.closeActionDropdown();
            });
        },
        saveApprovalSelections(payload = null) {
            const self = this;
            const internalIds = Array.isArray(payload && payload.internal_ids) ? payload.internal_ids : self.selectedAgentIds;
            const customerIds = Array.isArray(payload && payload.customer_ids) ? payload.customer_ids : self.selectedCustomerIds;
            const guestIds = Array.isArray(payload && payload.guest_ids) ? payload.guest_ids : self.selectedGuestIds;
            axios.post('api/workspace/tickets/' + self.$route.params.id + '/quick-actions', {
                action: 'approvals',
                internal_ids: internalIds,
                customer_ids: customerIds,
                guest_ids: guestIds,
            }).then(function (response) {
                if (!response.data.access) {
                    self.$router.push(self.ticketListPath);
                    return;
                }

                self.ticket = response.data.ticket;
                self.ticketReply.status_id = response.data.ticket.status_id;
                self.headerStatusId = response.data.ticket.status_id;
                self.headerPriorityId = response.data.ticket.priority_id;
                self.selectedTicketTopicId = response.data.ticket.ticket_topic_id;
                self.syncProjectSelection(response.data.ticket);
                self.selectedLabelId = response.data.ticket.labels && response.data.ticket.labels.length ? Number(response.data.ticket.labels[0].id) : null;
                self.selectedAgentIds = (response.data.ticket.agents || []).map(function (agent) {
                    return Number(agent.id);
                });
                if (self.selectedAgentIds.length === 0 && response.data.ticket.agent_id) {
                    self.selectedAgentIds = [Number(response.data.ticket.agent_id)];
                }
                self.selectedCustomerIds = (response.data.ticket.customers || []).map(function (customer) {
                    return Number(customer.id);
                }).filter(function (id) {
                    return id !== Number(response.data.ticket.user_id);
                });
                self.selectedGuestIds = (response.data.ticket.guests || []).map(function (guest) {
                    return Number(guest.id);
                });
                self.approvalModal = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        saveAgents() {
            const self = this;
            axios.post('api/workspace/tickets/' + self.$route.params.id + '/quick-actions', {
                action: 'agent',
                value: self.selectedAgentIds,
            }).then(function (response) {
                if (!response.data.access) {
                    self.$router.push(self.ticketListPath);
                    return;
                }
                self.ticket = response.data.ticket;
                self.ticketReply.status_id = response.data.ticket.status_id;
                self.headerStatusId = response.data.ticket.status_id;
                self.headerPriorityId = response.data.ticket.priority_id;
                self.selectedTicketTopicId = response.data.ticket.ticket_topic_id;
                self.syncProjectSelection(response.data.ticket);
                self.selectedAgentIds = (response.data.ticket.agents || []).map(function (agent) {
                    return Number(agent.id);
                });
                if (self.selectedAgentIds.length === 0 && response.data.ticket.agent_id) {
                    self.selectedAgentIds = [Number(response.data.ticket.agent_id)];
                }
                self.selectedLabelId = response.data.ticket.labels && response.data.ticket.labels.length ? Number(response.data.ticket.labels[0].id) : null;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        updateTicketStatus() {
            const self = this;
            if (!self.canManageStatus) {
                return;
            }

            axios.post('api/workspace/tickets/' + self.$route.params.id + '/quick-actions', {
                action: 'status',
                value: self.headerStatusId,
            }).then(function (response) {
                if (!response.data.access) {
                    self.$router.push(self.ticketListPath);
                    return;
                }
                self.ticket = response.data.ticket;
                self.ticketReply.status_id = response.data.ticket.status_id;
                self.headerStatusId = response.data.ticket.status_id;
                self.syncProjectSelection(response.data.ticket);
                self.selectedLabelId = response.data.ticket.labels && response.data.ticket.labels.length ? Number(response.data.ticket.labels[0].id) : null;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.headerStatusId = self.ticket.status_id || self.ticketReply.status_id;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        updateTicketPriority() {
            const self = this;
            if (!self.canViewPriority) {
                return;
            }

            axios.post('api/workspace/tickets/' + self.$route.params.id + '/quick-actions', {
                action: 'priority',
                value: self.headerPriorityId,
            }).then(function (response) {
                if (!response.data.access) {
                    self.$router.push(self.ticketListPath);
                    return;
                }
                self.ticket = response.data.ticket;
                self.ticketReply.status_id = response.data.ticket.status_id;
                self.headerStatusId = response.data.ticket.status_id;
                self.headerPriorityId = response.data.ticket.priority_id;
                self.syncProjectSelection(response.data.ticket);
                self.selectedLabelId = response.data.ticket.labels && response.data.ticket.labels.length ? Number(response.data.ticket.labels[0].id) : null;
                self.syncHeaderPrioritySelection();
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.headerPriorityId = self.ticket.priority_id;
                self.syncHeaderPrioritySelection();
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        updateTicketTopic() {
            const self = this;
            if (!self.canManageTicketTopic) {
                return;
            }

            axios.post('api/workspace/tickets/' + self.$route.params.id + '/quick-actions', {
                action: 'ticket_topic',
                value: self.selectedTicketTopicId,
            }).then(function (response) {
                if (!response.data.access) {
                    self.$router.push(self.ticketListPath);
                    return;
                }
                self.ticket = response.data.ticket;
                self.ticketReply.status_id = response.data.ticket.status_id;
                self.headerStatusId = response.data.ticket.status_id;
                self.headerPriorityId = response.data.ticket.priority_id;
                self.selectedTicketTopicId = response.data.ticket.ticket_topic_id;
                self.syncProjectSelection(response.data.ticket);
                self.selectedLabelId = response.data.ticket.labels && response.data.ticket.labels.length ? Number(response.data.ticket.labels[0].id) : null;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.selectedTicketTopicId = self.ticket.ticket_topic_id;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        saveCustomers() {
            const self = this;
            axios.post('api/workspace/tickets/' + self.$route.params.id + '/quick-actions', {
                action: 'customer',
                value: self.selectedCustomerIds,
            }).then(function (response) {
                if (!response.data.access) {
                    self.$router.push(self.ticketListPath);
                    return;
                }
                self.ticket = response.data.ticket;
                self.selectedCustomerIds = (response.data.ticket.customers || []).map(function (customer) {
                    return Number(customer.id);
                }).filter(function (id) {
                    return id !== Number(response.data.ticket.user_id);
                });
                self.syncProjectSelection(response.data.ticket);
                self.selectedLabelId = response.data.ticket.labels && response.data.ticket.labels.length ? Number(response.data.ticket.labels[0].id) : null;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        saveProjectAssignments() {
            const self = this;
            axios.post('api/workspace/tickets/' + self.$route.params.id + '/quick-actions', {
                action: 'project',
                value: self.normalizeProjectIds({project_ids: self.selectedProjectIds}),
            }).then(function (response) {
                if (!response.data.access) {
                    self.$router.push(self.ticketListPath);
                    return;
                }
                self.ticket = response.data.ticket;
                self.syncProjectSelection(response.data.ticket);
                self.selectedLabelId = response.data.ticket.labels && response.data.ticket.labels.length ? Number(response.data.ticket.labels[0].id) : null;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.syncProjectSelection(self.ticket);
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        getUserPhone(user) {
            return user.phone || user.phone_mobile || user.phone_landline || null;
        },
        deleteTicket() {
            const self = this;
            axios.delete('api/workspace/tickets/' + self.$route.params.id).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.$router.push(self.ticketListPath);
            }).catch(function () {
                self.deleteTicketModal = false;
            });
        }
    }
}
</script>
